import styles from './styles.module.scss'
import FilterSidePanel from './FiltersSidePanel'
import { prefixLocale } from '@helpers/env'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { RootState } from '@store/redux/reducers'
import { FilterState } from '@store/redux/reducers/filterReducer'
import { FilterOption } from 'types/filters'
import { FilterBuild } from './FiltersSidePanel/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCollectionFiltersState } from '../helpers'
import { getCustomerSavedFitFromCookies } from '@helpers/customer/fit'
import { createFilterCookies, getFiltersOptionsState } from '@helpers/filters'
import { COOKIE_EXPIRE_3_MONTHS, ORDERED_OPTIONS } from '@consts/index'
import FilterResult from './FilterResult'
import { removeCookie, setCookie } from '@helpers/session'
import { initFit } from '@helpers/initFit'
import trackError from '@helpers/trackError'
import { CollectionCategoriesFilters } from './CollectionCategoriesFilters'
import { updateFilterBuild } from '@helpers/collections/adapter/updateFilterBuild'

type NewCollectionFilterProps = {
  filterCategory: string
  filtersCategories: string[]
  filterBuild: FilterBuild
  onApplyFitToggle: (state: boolean) => void
  onFilterClear: () => void
  onFilterApply: (args: FilterOption[]) => void
  totalResults: number
  isMultiCollection: boolean
}

export default function CollectionFilter({
  filterCategory,
  filtersCategories,
  filterBuild,
  onFilterClear,
  onApplyFitToggle,
  onFilterApply: handleFilterApply,
  totalResults,
  isMultiCollection
}: NewCollectionFilterProps) {
  const router = useRouter()
  const filterOptions = useSelector<RootState, FilterState['filterOptions']>(
    state => state.filters.filterOptions
  )
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<
    FilterOption[]
  >([])
  const [ready, setReady] = useState(false) // first render

  const handleCategoryFilterClick = useCallback(
    (value: string) => {
      const pathAs = `${prefixLocale()}${router.route.replace(
        '/[collectionHandle]',
        ''
      )}/${router.query.collectionHandle}?category=${value}`
      router.push(
        {
          query: {
            ...router.query,
            patname: router.pathname,
            category: value
          }
        },
        pathAs,
        { shallow: true }
      )
    },
    [router]
  )

  const filterBuildState = useMemo(
    () => updateFilterBuild(filterBuild, selectedFilterOptions),
    [filterBuild, selectedFilterOptions]
  )

  useEffect(() => {
    if (!ready) return
    const newFilters = createFilterCookies(filterOptions)
    setCookie('filters', newFilters, COOKIE_EXPIRE_3_MONTHS)
    removeCookie('th_ftv')
  }, [JSON.stringify(filterOptions)])

  useEffect(() => {
    // fetch the cookie and update dynamic filter
    try {
      const parsedCookie = initFit()

      const filterOptionsFromCookie: FilterOption[] = Object.keys(
        parsedCookie
      ).reduce<FilterOption[]>(
        (arr, filterKey) => [
          ...arr,
          {
            type: filterKey,
            value: parsedCookie[filterKey]?.toString() || '',
            position:
              1 +
              ORDERED_OPTIONS.findIndex(
                option => filterKey === option.toLowerCase()
              )
          }
        ],
        []
      )
      setReady(true)
      setSelectedFilterOptions(filterOptionsFromCookie)
      handleFilterApply(filterOptionsFromCookie)
    } catch (e) {
      trackError(e as Error)
    }
  }, [])

  const handleFilterSelect = () => {
    return (fitAttribute: string) => {
      return (selectedOption: string) => {
        setSelectedFilterOptions(prev =>
          getCollectionFiltersState(prev, fitAttribute, selectedOption)
        )
      }
    }
  }

  const handleFilterClear = useCallback(() => {
    setSelectedFilterOptions([])
    onFilterClear()
    onApplyFitToggle(false)
  }, [onFilterClear])

  const handleApplyFitToggle = useCallback(
    (state: boolean) => {
      if (!state) return setSelectedFilterOptions([])

      const savedFitFromCookies = getCustomerSavedFitFromCookies()
      setSelectedFilterOptions(
        savedFitFromCookies ? getFiltersOptionsState(savedFitFromCookies) : []
      )
      onApplyFitToggle(state)
    },
    [onApplyFitToggle]
  )

  const onHandleFilterApply = () => {
    handleFilterApply(selectedFilterOptions)
  }
  return (
    <div className={styles.filter__container}>
      {isMultiCollection && (
        <>
          <div className={styles.multiFilters__navWrapper}>
            <CollectionCategoriesFilters
              filtersCategories={filtersCategories}
              selectedCategory={filterCategory}
              onSelect={handleCategoryFilterClick}
            />
          </div>
          <FilterResult
            totalResults={totalResults}
            selectedFilterOptions={filterOptions}
          />
        </>
      )}
      <FilterSidePanel
        filters={filterBuildState}
        selectedFilterOptions={selectedFilterOptions}
        onFilterChange={handleFilterSelect}
        onFilterApply={onHandleFilterApply}
        onFilterClear={handleFilterClear}
        onApplyFitToggle={handleApplyFitToggle}
      />
    </div>
  )
}
